import { TransactionLimitData } from "../components/controls/TransactionLimit/TransactionLimit";
import {
  Result,
  UserTransactionLimit,
  TransactionTypes,
} from "../models/interfaces";
import { DashBoardData } from "../models/props";
import { BaseService } from "./BaseService";

export type Role = {
  id?: number;
  name?: string;
  description?: string;
};

export type User = {
  id?: number;
  userName?: string;
  userFullName?: string;
  approvingOfficer?: boolean;
  transactionLimit?: number;
  password?: string;
  businessId?: number;
  transactionLimitType?: number;
  email?: string;
  phoneNo?: string;
  transactionTypes?: string[];
  permissions?: string[];
  businessOwnerId?: number;
  transactionLimitPeriodType?: number;
  dateEntered?: Date;
  dateEdited?: Date;
  enteredBy?: string;
  modifiedBy?: string;
  uid?: string;
  isDisabled?: boolean;
  userProfileType?: number;
  mustChangePassword?: boolean;
  mustSetupSecurityQuestion?: boolean;
  branchId?: number;
};

class UserService extends BaseService {
  async deleteUserTransactionLimit(
    limit: UserTransactionLimit,
    userId: number
  ): Promise<Result<boolean>> {
    return this.delete(`users/${userId}`, {
      periodType: limit.period,
      transactionTypeCode: limit.transactionType,
    });
  }

  async getTransactionLimits(
    userId: number
  ): Promise<Result<UserTransactionLimit[]>> {
    return this.get(`users/${userId}/transaction-limit`);
  }

  async setUserTransactionLimit(
    limit: TransactionLimitData,
    userId: number
  ): Promise<Result<UserTransactionLimit>> {
    return this.post(`users/${userId}/transaction-limit`, {
      periodType: limit.period,
      transactionTypeCode: limit.transactionType,
      amount: limit.amount,
    });
  }

  async getDashBoardData(): Promise<Result<DashBoardData>> {
    return await this.get(`users/dashboard`);
  }

  async getUsers(params: {
    userId?: number;
    fullName?: string;
    loginId?: string;
    email?: string;
    phoneNo?: string;
    pageIndex?: number;
    recordCount?: number;
  }): Promise<Result<User[]>> {
    return this.get<Result<User[]>>("users", params);
  }

  async getUser(params: any) {
    return await super.get<Result<User>>(`users/${params.id}`);
  }

  async registerUser(params: User) {
    if (params === undefined) throw new Error("Invalid user parameters");
    return await this.post<Result<User>>(`users`, params);
  }

  async updateUserProfile(id: number, params: User) {
    if (params === undefined) throw new Error("Invalid user parameters");
    return await this.put<Result<User>>(`users/${id}`, params);
  }

  async deleteUserProfile(id: number): Promise<Result<boolean>> {
    return this.delete("users/" + id);
  }

  async setUserRoles(userid: number, roleids: number[]) {
    return this.post("users/" + userid + "/roles", roleids);
  }

  async getRoles(userid: number): Promise<Result<Role[]>> {
    const resp = await this.get<Result<Role[]>>(`users/${userid}/roles`);
    return resp;
  }

  async getTransactionTypes(userid: number): Promise<any> {
    return this.get<TransactionTypes>(`users/${userid}/transaction-types`);
  }
}

export default new UserService();
